import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllHotels, getProperties } from '../../services/g2TravelApi';
import {
  HOTEL_SECTION_PLACEHOLDER_LARGE,
  resolveHotelSectionImage,
} from '../../lib/hotelSectionImages';
import { buildHotelDetailPath } from '../../lib/hotelRoutes';
import './UniqueProperties.css';

const UniqueProperties = ({ onViewHotel }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const carouselRef = useRef(null);

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get rating text based on score
  const getRatingText = (score) => {
    if (score >= 9.5) return 'Exceptional';
    if (score >= 9.0) return 'Wonderful';
    if (score >= 8.5) return 'Excellent';
    if (score >= 8.0) return 'Very Good';
    if (score >= 7.0) return 'Good';
    return 'Pleasant';
  };

  // Generate review counts similar to the image
  const getReviewCount = (index) => {
    const counts = [75, 120, 42, 774, 156, 89, 203, 312];
    return counts[index % counts.length];
  };

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        console.log('🔍 Fetching unique properties...');

        let baseHotels = [];

        // Try full hotels endpoint first
        try {
          const allHotelsResponse = await getAllHotels({});
          if (allHotelsResponse?.success && Array.isArray(allHotelsResponse.hotels) && allHotelsResponse.hotels.length > 0) {
            baseHotels = allHotelsResponse.hotels;
          }
        } catch (err) {
          console.warn('⚠️ getAllHotels failed, falling back to getProperties:', err);
        }

        // Fallback to static properties list if needed
        if (!baseHotels.length) {
          const propertiesData = await getProperties();
          if (Array.isArray(propertiesData) && propertiesData.length > 0) {
            baseHotels = propertiesData;
          }
        }

        if (!Array.isArray(baseHotels) || baseHotels.length === 0) {
          console.warn('⚠️ No properties found from G2 Travel API');
          setProperties([]);
          return;
        }

        // Take first 8 properties for the carousel
        const selectedProperties = baseHotels.slice(0, 8);

        // Prepare images for each property (no external API)
        console.log('📸 Preparing images for unique properties...');
        const propertiesWithData = selectedProperties.map((property, index) => {
          const propertyName =
            property.name || property.propertyName || property.hotelName || 'Property';
          const imageUrl = resolveHotelSectionImage(property, index, 'large');

          const rating = 9.0 + Math.random() * 0.6;
          const roundedRating = Math.round(rating * 10) / 10;

          return {
            id: property.id || property.propertyId || property.code || `property-${index}`,
            name: propertyName,
            image: imageUrl,
            city: property.cityName || property.city || property.location || '',
            country: property.countryName || property.country || 'United Kingdom',
            rating: roundedRating,
            ratingText: getRatingText(roundedRating),
            reviewCount: getReviewCount(index),
            propertyData: property,
          };
        });

        console.log(`✅ Loaded ${propertiesWithData.length} unique properties with images`);
        setProperties(propertiesWithData);
      } catch (error) {
        console.error('❌ Error fetching unique properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const handleNext = () => {
    const maxIndex = Math.max(0, properties.length - 4);
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePropertyClick = (property) => {
    if (onViewHotel && property.id) {
      onViewHotel(property.id, property.propertyData);
    } else {
      navigate(buildHotelDetailPath(property.id));
    }
  };

  // On mobile, show all properties for horizontal scrolling
  const visibleProperties = isMobile ? properties : properties.slice(currentIndex, currentIndex + 4);
  const canGoNext = !isMobile && currentIndex < properties.length - 4;

  if (loading) {
    return (
      <section className="unique-properties">
        <div className="unique-properties__container">
          <div className="unique-properties__header">
            <h2 className="unique-properties__title">Stay at our top unique properties</h2>
            <p className="unique-properties__subtitle">Loading properties...</p>
          </div>
        </div>
      </section>
    );
  }

  if (properties.length === 0) {
    return null;
  }

  return (
    <section className="unique-properties">
      <div className="unique-properties__container">
        <div className="unique-properties__header">
          <h2 className="unique-properties__title">Stay at our top unique properties</h2>
          <p className="unique-properties__subtitle">
            From castles and villas to boats and igloos, we have it all.
          </p>
        </div>

        <div className="unique-properties__wrapper" ref={carouselRef}>
          <div className="unique-properties__cards">
            {visibleProperties.map((property) => (
              <div
                key={property.id}
                className="unique-properties__card"
                onClick={() => handlePropertyClick(property)}
              >
                <div className="unique-properties__card-image-wrapper">
                  <img
                    src={property.image}
                    alt={property.name}
                    className="unique-properties__card-image"
                    onError={(e) => {
                      e.target.src = HOTEL_SECTION_PLACEHOLDER_LARGE;
                    }}
                  />
                </div>
                <div className="unique-properties__card-content">
                  <h3 className="unique-properties__card-name">{property.name}</h3>
                  <p className="unique-properties__card-location">
                    {property.country}, {property.city}
                  </p>
                  <div className="unique-properties__card-rating">
                    <span className="unique-properties__card-rating-score">{property.rating}</span>
                    <span className="unique-properties__card-rating-text">{property.ratingText}</span>
                  </div>
                  <p className="unique-properties__card-reviews">{property.reviewCount} reviews</p>
                </div>
              </div>
            ))}
          </div>

          {canGoNext && (
            <button
              className="unique-properties__arrow unique-properties__arrow--right"
              onClick={handleNext}
              aria-label="Next properties"
            >
              ›
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default UniqueProperties;
